/**
 * Gleamer's own strings, in the `Gleamer` i18n namespace: English and French. OHIF's languages come from
 * `@ohif/i18n`; these are added to it at start (registerGleamerLocales, from the gdata extension).
 *
 * Keys are ids, not the English text: the messages hold `:`, which i18next reads as a namespace separator.
 */
export const GLEAMER_NS = 'Gleamer';

const EN = {
  'validation.chooseOneOf': '{{field}}: choose one of {{options}}',
  'validation.expects.string': '{{field}}: expects a text',
  'validation.expects.number': '{{field}}: expects a number',
  'validation.expects.integer': '{{field}}: expects a whole number',
  'validation.expects.boolean': '{{field}}: expects yes or no',
  'validation.expects.array': '{{field}}: expects a list',
  'validation.expects.object': '{{field}}: not filled as expected',
  // a boolean choice without titles in the schema
  'validation.true': 'yes',
  'validation.false': 'no',
  'review.heldCase':
    'You still hold a case to label in the task {{task}}: finish it before reviewing.',
  'review.openHeldCase': 'Open the case to label',
  'step.saveLabelPass': 'Could not save the label pass',
  'step.nextCase': 'Could not get the next case',
  'step.finishReview': 'Could not finish the review session',
};

export type GleamerKey = keyof typeof EN;

const FR: Record<GleamerKey, string> = {
  'validation.chooseOneOf': '{{field}} : choisissez parmi {{options}}',
  'validation.expects.string': '{{field}} : texte attendu',
  'validation.expects.number': '{{field}} : nombre attendu',
  'validation.expects.integer': '{{field}} : nombre entier attendu',
  'validation.expects.boolean': '{{field}} : oui ou non attendu',
  'validation.expects.array': '{{field}} : liste attendue',
  'validation.expects.object': '{{field}} : réponse non conforme',
  'validation.true': 'oui',
  'validation.false': 'non',
  'review.heldCase':
    'Vous avez encore un cas à labelliser dans la tâche {{task}} : terminez-le avant la review.',
  'review.openHeldCase': 'Ouvrir le cas à labelliser',
  'step.saveLabelPass': "Impossible d'enregistrer le pass",
  'step.nextCase': "Impossible d'obtenir le cas suivant",
  'step.finishReview': 'Impossible de terminer la session de review',
};

export const gleamerLocales = { 'en-US': { [GLEAMER_NS]: EN }, fr: { [GLEAMER_NS]: FR } };

/** Adds Gleamer's strings to OHIF's i18n (`@ohif/i18n`'s addLocales). */
export function registerGleamerLocales(i18n: {
  addLocales: (locales: typeof gleamerLocales) => void;
}) {
  i18n.addLocales(gleamerLocales);
}

type Values = Record<string, string | number>;
type TFunction = (key: string, options?: Record<string, unknown>) => string;

function interpolate(template: string, values: Values) {
  return template.replace(/{{(\w+)}}/g, (_, name) => String(values[name] ?? ''));
}

/** The English text of a Gleamer string: what `gleamerT` falls back to, and what code without a `t` uses. */
export function gleamerEnglish(key: GleamerKey, values: Values = {}) {
  return interpolate(EN[key], values);
}

/**
 * A Gleamer string in the user's language. Falls back to the English text when i18n is not ready or misses the
 * key (tests, a language without Gleamer strings).
 */
export function gleamerT(t: TFunction | undefined, key: GleamerKey, values: Values = {}) {
  const english = gleamerEnglish(key, values);
  return t ? t(key, { ...values, ns: GLEAMER_NS, defaultValue: english }) || english : english;
}
